/**
 * Iter-291 — QR Scanner modal
 *
 * Wraps html5-qrcode in a Shadcn Dialog. Used by:
 *   • /talent/qr-scan (standalone page)
 *   • Inline button on the talent CheckIn screen (#qr-scan-btn)
 *
 * Props:
 *   open            : boolean
 *   onClose         : () => void
 *   onScan(text)    : called with the decoded text. We DON'T close ourselves;
 *                     the parent decides whether to close + navigate.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, Camera, Type, XCircle } from 'lucide-react';

let qrLib = null;

async function loadLib() {
  if (qrLib) return qrLib;
  // Lazy load — html5-qrcode is ~80 KB.
  qrLib = await import('html5-qrcode');
  return qrLib;
}

export default function QRScannerModal({ open, onClose, onScan }) {
  const containerId = 'qr-scanner-container';
  const scannerRef = useRef(null);
  const scannerStartedRef = useRef(false);   // Iter-291 — only call .stop() if start() resolved.
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [manualToken, setManualToken] = useState('');

  useEffect(() => {
    if (!open || manualMode) return undefined;
    let cancelled = false;
    setStarting(true);
    setError(null);
    scannerStartedRef.current = false;

    (async () => {
      try {
        const { Html5Qrcode } = await loadLib();
        if (cancelled) return;
        const scanner = new Html5Qrcode(containerId);
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decodedText) => {
            // First successful scan — pause and hand off.
            try { scanner.pause(); } catch (_) { /* noop */ }
            onScan(decodedText);
          },
          // ignore per-frame failures silently
          () => {},
        );
        if (cancelled) {
          // Caller unmounted between await and now — stop immediately.
          try { await scanner.stop(); } catch (_) { /* noop */ }
          try { scanner.clear(); } catch (_) { /* noop */ }
          return;
        }
        scannerStartedRef.current = true;
      } catch (e) {
        // Camera permission denied, no camera, or already-running collision.
        if (!cancelled) {
          setError('Camera unavailable. You can paste the QR code text instead.');
          setManualMode(true);
        }
      } finally {
        if (!cancelled) setStarting(false);
      }
    })();

    return () => {
      cancelled = true;
      const scanner = scannerRef.current;
      const wasStarted = scannerStartedRef.current;
      scannerRef.current = null;
      scannerStartedRef.current = false;
      if (!scanner) return;
      // Guard .stop() — calling it before a successful start() throws
      // "Cannot stop, scanner is not running or paused." (html5-qrcode quirk).
      if (wasStarted) {
        scanner.stop().catch(() => {}).finally(() => {
          try { scanner.clear(); } catch (_) { /* noop */ }
        });
      } else {
        try { scanner.clear(); } catch (_) { /* noop */ }
      }
    };
  }, [open, manualMode, onScan]);

  const submitManual = () => {
    const v = manualToken.trim();
    if (!v) return;
    onScan(v);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="qr-scanner-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-cyan-600" /> Scan event QR
          </DialogTitle>
          <DialogDescription>
            Point your camera at the QR code displayed at your event.
          </DialogDescription>
        </DialogHeader>

        {!manualMode && (
          <div className="space-y-3" data-testid="qr-scanner-camera-mode">
            <div className="relative rounded-md overflow-hidden bg-black aspect-square">
              <div id={containerId} className="w-full h-full" />
              {starting && (
                <div className="absolute inset-0 flex items-center justify-center text-white">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              )}
            </div>
            {error && (
              <p className="text-xs text-rose-600 inline-flex items-center gap-1">
                <XCircle className="w-3.5 h-3.5" /> {error}
              </p>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setManualMode(true)}
              className="w-full"
              data-testid="qr-scanner-manual-toggle"
            >
              <Type className="w-4 h-4 mr-1.5" /> Enter code manually
            </Button>
          </div>
        )}

        {manualMode && (
          <div className="space-y-3" data-testid="qr-scanner-manual-mode">
            <Input
              value={manualToken}
              onChange={(e) => setManualToken(e.target.value)}
              placeholder="Paste the QR token from the printed sign"
              data-testid="qr-scanner-manual-input"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') submitManual(); }}
            />
            <div className="flex justify-between gap-2">
              <Button variant="outline" size="sm" onClick={() => setManualMode(false)} data-testid="qr-scanner-back-to-camera">
                <Camera className="w-4 h-4 mr-1.5" /> Use camera
              </Button>
              <Button size="sm" onClick={submitManual} disabled={!manualToken.trim()} data-testid="qr-scanner-manual-submit">
                Submit
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
